// Option rows for the entry screens (onboarding, the Profil role switch):
// the whole row is the 44px target, so the control itself is the row.
//   CheckRow    role="checkbox" (Space and Enter toggle, as a button does)
//   ChoiceRows  role="radiogroup" of role="radio" rows: one tab stop, the
//               arrow keys move and select (WAI-ARIA radio group pattern)
// The box and the dot are drawn by spans, so nothing renders under 44px.
import { useRef, type KeyboardEvent, type ReactNode } from "react";

export function CheckRow({ id, checked, onChange, invalid, describedBy, children }: { id: string; checked: boolean; onChange: (next: boolean) => void; invalid?: boolean; describedBy?: string; children: ReactNode }) {
  return (
    <button
      id={id}
      type="button"
      role="checkbox"
      className="choice-row"
      aria-checked={checked}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onClick={() => onChange(!checked)}
    >
      <span className="choice-box" data-kind="check" aria-hidden="true" />
      <span className="choice-text">{children}</span>
    </button>
  );
}

export interface ChoiceOption<T extends string> {
  id: T;
  label: string;
  hint?: string;
}

export function ChoiceRows<T extends string>({ labelledBy, idBase, options, value, onChange }: { labelledBy: string; idBase: string; options: ChoiceOption<T>[]; value: T | null; onChange: (next: T) => void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const at = options.findIndex((o) => o.id === value);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = i;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = (i + 1) % options.length;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = (i - 1 + options.length) % options.length;
    else return;
    e.preventDefault();
    onChange(options[next]!.id);
    refs.current[next]?.focus();
  };
  return (
    <div className="choice-rows" role="radiogroup" aria-labelledby={labelledBy}>
      {options.map((o, i) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            id={`${idBase}-${o.id}`}
            type="button"
            role="radio"
            className="choice-row"
            data-boxed=""
            aria-checked={on}
            tabIndex={on || (at < 0 && i === 0) ? 0 : -1}
            onClick={() => onChange(o.id)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <span className="choice-box" data-kind="radio" aria-hidden="true" />
            <span className="choice-text">
              <span className="choice-label">{o.label}</span>
              {o.hint ? <span className="choice-hint">{o.hint}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
